import { Link } from '@tanstack/react-router';

import {
  THRESHOLD_CHIP,
  THRESHOLD_CHIP_BOX,
  THRESHOLD_LABEL,
} from '@/features/workspace-usage';
import type { LimitState } from '../limitState';

export interface WorkspaceLimitChipProps {
  workspaceId: string;
  workspaceName: string;
  /** The workspace's state, resolved once for the whole shell. */
  state: LimitState;
}

/**
 * The workspace's spend state, wherever you are in it (spec 101 US3).
 *
 * A chip, deliberately, and deliberately not a banner. A full-width band on
 * every page is wallpaper within a day and is then unread on the day it
 * changes; a chip that is absent almost all the time cannot become wallpaper,
 * which is why it is also not dismissible (FR-016).
 *
 * Silent below `approaching`, and silent when nothing is known. Both absences
 * are absences, not claims: nothing here ever says a workspace is within its
 * limit, because work on a model with no price spends without ever reaching a
 * cap (FR-023).
 *
 * Speaks only from the usage figures. A refused run proves a limit was reached
 * at some moment that has passed and can never distinguish `approaching`, and
 * this marker is the one surface that follows a person across the whole
 * application — it has to be the current reading or it is worse than nothing.
 * That also makes it, in practice, an administrator's marker: they are the only
 * people the figures answer for (FR-029, FR-030).
 */
export function WorkspaceLimitChip({
  workspaceId,
  workspaceName,
  state,
}: WorkspaceLimitChipProps) {
  if (!state.known || state.source !== 'figures') return null;
  if (state.state === 'under') return null;

  const label = THRESHOLD_LABEL[state.state];

  return (
    <Link
      to="/workspaces/$workspaceId/ai-usage/spend"
      params={{ workspaceId }}
      // The bare word out of a top bar spanning every page says nothing about
      // whose limit it is (FR-017). The visible chip stays short; the name
      // carries the workspace.
      aria-label={`${workspaceName}: ${label}. View usage.`}
      /*
        The usage page's own colour and its own word for this state, imported
        rather than restated. Two surfaces describing one state differently is
        how a reader learns to trust neither, and the word is there in any case
        because colour never travels alone (FR-014, FR-021).
      */
      className={`${THRESHOLD_CHIP[state.state]} ${THRESHOLD_CHIP_BOX}`}
    >
      {label}
    </Link>
  );
}
